import {
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeSlash,
} from "nucleo-ui-outline-18"

import type { HelpCenterTab } from "@productlane/zero"
import { Badge, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { mutateOk, useZero } from "@/lib/zero"

export const TAB_COPY = {
	badge: "Draft",
	badgeTooltip: "Readers can't see this tab yet. Publish it from the tab menu.",
	publish: "Publish tab",
	unpublish: "Unpublish tab",
	published: (name: string) => `Published "${name}"`,
	unpublished: (name: string) => `Unpublished "${name}"`,
	publishFailed: "Couldn't publish the tab",
	unpublishFailed: "Couldn't unpublish the tab",
	deleteGoesLive:
		"Its groups and articles move to the first tab, where readers can see them.",
}

/** The menu entry that flips a tab between published and unpublished. */
export function publishTabAction(tab: Pick<HelpCenterTab, "isPublished">) {
	return tab.isPublished
		? { label: TAB_COPY.unpublish, Icon: EyeSlash }
		: { label: TAB_COPY.publish, Icon: Eye }
}

export function usePublishDocsTab() {
	const zero = useZero()
	return async (
		tab: Pick<HelpCenterTab, "id" | "name">,
		isPublished: boolean,
	) => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.helpCenterTabs.update({ id: tab.id, isPublished }),
				),
				"server",
			)
			toast.success({
				title: isPublished
					? TAB_COPY.published(tab.name)
					: TAB_COPY.unpublished(tab.name),
			})
		} catch (error) {
			toast.error({
				title: isPublished ? TAB_COPY.publishFailed : TAB_COPY.unpublishFailed,
				description: error instanceof Error ? error.message : undefined,
			})
		}
	}
}

/** Marks a tab readers can't see yet, so nobody mistakes it for a live one. */
export function UnpublishedTabBadge() {
	return (
		<Tooltip label={TAB_COPY.badgeTooltip}>
			<Badge variant="amber" size="sm" className="flex-none">
				{TAB_COPY.badge}
			</Badge>
		</Tooltip>
	)
}
